import React, { FunctionComponent, useEffect } from 'react';
import Field from 'components/Form/Field';
import SingleSelect, { IOption } from 'components/Form/Select/SingleSelect';
import { FormikHelpers as FormikActions } from 'formik';
import useGetBuildings from './useGetBuildings';
import useGetUnits from './useGetUnits';
import styled from 'styled-components';
import Text from 'components/Text';

interface IValues {
  building_id?: number;
  unit_id?: number;
}

interface IUnitTemplate {
  guid?: string;
  buildingId?: number | null;
  setFieldValue: FormikActions<IValues>['setFieldValue'];
  isClearable?: boolean;
  prepareUnit?: IOption;
  unitPlaceholder?: string;
  unitLabel?: string;
  buildingLabel?: string;
  buildingPlaceholder?: string;
  selectIfExistOne?: boolean;
  values: IValues;
  onChange?: () => void;
}

const UnitTemplate: FunctionComponent<IUnitTemplate> = ({
  guid,
  values,
  setFieldValue,
  buildingPlaceholder,
  unitPlaceholder,
  onChange,
  isClearable = true,
  buildingLabel = 'Building',
  unitLabel = 'Unit',
}) => {
  const { buildings } = useGetBuildings(guid);
  const { units, fetchUnits, setUnits } = useGetUnits<IOption>({ guid });

  useEffect(() => {
    if (buildings.length === 1 && !values.building_id) {
      setFieldValue('building_id', buildings[0].id);
    }
  }, [buildings, values.building_id, setFieldValue]);

  useEffect(() => {
    if (values.building_id) {
      fetchUnits({ buildingId: values.building_id, guid });
    } else {
      setUnits([]);
      if (values.unit_id) {
        setFieldValue('unit_id', null);
      }
    }
  }, [values.building_id, fetchUnits, setUnits, guid]);

  return (
    <>
      <Field
        name="building_id"
        isClearable={isClearable}
        component={SingleSelect}
        alphabeticallySort={false}
        label={buildingLabel}
        placeholder={buildingPlaceholder}
        options={buildings}
      />
      <UnitWrapper>
        <Field
          name="unit_id"
          isClearable={isClearable}
          component={UnitSingleSelect}
          alphabeticallySort={false}
          label={unitLabel}
          placeholder={unitPlaceholder}
          options={units}
          onChange={onChange}
          isDisabled={!values.building_id}
        />
        <Optional size="xs">
          <sup>*</sup>Select Building first
        </Optional>
      </UnitWrapper>
    </>
  );
};

export default UnitTemplate;

const UnitSingleSelect = styled(SingleSelect)`
  margin-bottom: 0px;
`;

const UnitWrapper = styled.div`
  margin-bottom: 18px;
`;

const Optional = styled(Text)`
  color: ${(props) => props.theme.colors.darkGrey};
  line-height: 14px;
  margin-left: 10px;
`;
